<template>
  <div
    data-cy="select-options-list"
    class="overflow-y-auto flex-1 min-h-0"
    :class="optionsClass"
    :style="listStyle"
  >
    <div
      v-for="(option, index) in options"
      :key="option.value"
      :class="{
        'rounded-t-lg': index === 0,
        'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600':
          !isOptionDisabled(option.value),
        'opacity-50 cursor-not-allowed': isOptionDisabled(option.value)
      }"
    >
      <div
        v-if="multiselect"
        class="px-4 py-2 pt-3 flex items-center"
        @click.stop="emit('toggle', option, !isOptionSelected(option.value))"
      >
        <div class="w-4 h-4 relative flex items-center justify-center">
          <input
            :id="`checkbox-${option.value}`"
            type="checkbox"
            :checked="isOptionSelected(option.value)"
            :disabled="isOptionDisabled(option.value)"
            class="w-4 h-4 text-blue-600 bg-gray-100 border border-gray-300 rounded pointer-events-none focus:ring-0 dark:bg-gray-700 dark:border-gray-600 checked:bg-blue-600 checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:border-blue-600"
          />
          <svg
            v-if="isOptionSelected(option.value)"
            class="w-3 h-3 text-white absolute pointer-events-none"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <label
          class="w-full ml-2 mt-2 text-sm font-medium text-gray-900 dark:text-gray-300"
          >{{ option.label }}</label
        >
      </div>

      <div
        v-else
        class="px-4 py-2 w-full flex items-center justify-between"
        :class="{
          'font-bold': option.value === modelValue && modelValue,
          'text-black': option.value === modelValue && modelValue,
          'font-normal': option.value !== modelValue && modelValue,
          'text-gray-700 dark:text-gray-300':
            option.value !== modelValue && modelValue
        }"
        @click="emit('pick', option)"
      >
        <slot name="option" :option="option">
          <span>{{ option.label }}</span>
        </slot>
        <svg
          v-if="option.value === modelValue && modelValue"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="black"
        >
          <path
            fill-rule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </div>
  </div>

  <div
    v-if="showNewItem"
    class="shrink-0 border-t mt-.75 border-gray-200 dark:border-gray-600"
  ></div>

  <div
    v-if="showNewItem"
    ref="newItemRow"
    class="shrink-0 px-4 py-2.5 border-t dark:border-gray-600 text-blue-600 dark:text-blue-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
    @mousedown.stop.prevent="emit('add-item')"
  >
    + Add New Item
  </div>
</template>

<script lang="ts">
/** A row in the list; `value` is null for the placeholder row a single-select shows first. */
export interface SelectInputOption {
  value: string | number | null;
  label: string;
}

/** Estimated rendered height of the "Add New Item" row; see rowDrift.ts. */
export const NEW_ITEM_ROW_HEIGHT = 42;
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";

import { warnOnRowDrift } from "./rowDrift";

type OptionValue = string | number;

// The option rows of a SelectInput (checkbox rows for multi-select, plain rows
// otherwise) and the "Add New Item" row, rendered identically inside the
// anchored panel and inside the bottom sheet. Multi-root on purpose: the host
// lays these out as flex children, and a wrapper element would break that.
const props = withDefaults(
  defineProps<{
    options: SelectInputOption[];
    modelValue?: OptionValue | OptionValue[] | null;
    selectedValues?: OptionValue[];
    multiselect?: boolean;
    maxSelections?: number | null;
    showNewItem?: boolean;
    /** Cap on the scroll list; null leaves scrolling to the host, as a sheet does. */
    listMaxHeight?: number | null;
    optionsClass?: string;
  }>(),
  {
    modelValue: null,
    selectedValues: () => [],
    multiselect: false,
    maxSelections: null,
    showNewItem: false,
    listMaxHeight: null,
    optionsClass: ""
  }
);

const emit = defineEmits<{
  (e: "pick", option: SelectInputOption): void;
  (e: "toggle", option: SelectInputOption, checked: boolean): void;
  (e: "add-item"): void;
}>();

const newItemRow = ref<HTMLElement | null>(null);

const listStyle = computed(() =>
  props.listMaxHeight === null
    ? undefined
    : { maxHeight: `${props.listMaxHeight}px` }
);

const isOptionSelected = (value: OptionValue | null) => {
  if (!value) return false;
  return props.selectedValues.includes(value);
};

const atMaxSelections = computed(
  () =>
    Boolean(props.maxSelections) &&
    props.selectedValues.length >= (props.maxSelections as number)
);

const isOptionDisabled = (value: OptionValue | null) =>
  atMaxSelections.value && !isOptionSelected(value);

if (import.meta.env.DEV) {
  onMounted(() =>
    nextTick(() =>
      warnOnRowDrift(
        newItemRow.value,
        NEW_ITEM_ROW_HEIGHT,
        "NEW_ITEM_ROW_HEIGHT"
      )
    )
  );
}
</script>
